import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Save, CheckCircle2 } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useFetchPermissionById, useUpdatePermissions } from '../services'
import { makeEmpty, permissionSectors, PermissionsState } from '../util/utils'
import { toast } from '@/lib/hooks/use-toast'
import Spinner from '@/components/ui/spinner'
import { PageLayout } from '@/layouts'

export default function PermissionsManager({ id_user = '' }: { id_user?: string }) {
  const { data: permissionsData, isLoading: isFetching, isError } = useFetchPermissionById(id_user)
  const { mutate: updatePermissions } = useUpdatePermissions(id_user)
  const [permissions, setPermissions] = useState<PermissionsState | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // 2) useEffect também antes de qualquer return
  useEffect(() => {
    if (!isFetching) {
      setPermissions(permissionsData ?? makeEmpty(id_user))
    }
  }, [isFetching, permissionsData, id_user])

  // 3) Early-returns apenas depois de todos os hooks
  if (isFetching || permissions === null) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }
  if (isError) {
    return <div>Erro ao carregar permissões. Tente novamente mais tarde.</div>
  }

  // 4) A partir daqui, você pode usar updatePermissions e isSaving com segurança
  const handleSave = () => {
    setIsSaving(true)

    updatePermissions(permissions, {
      onSuccess: () => {
        toast({ description: 'Permissões salvas com sucesso!' })
        setIsSaving(false)
      },
      onError: () => {
        toast({ variant: 'destructive', description: 'Houve um erro ao salvar as permissões.' })
        setIsSaving(false)
      }
    })
  }

  const handlePermissionChange = (key: keyof PermissionsState, value: boolean) => {
    setPermissions(prev => {
      if (!prev) return prev
      return {
        ...prev,
        [key]: value
      }
    })
  }

  const getSectorStatus = (sectorPermissions: string[]) => {
    const enabledCount = sectorPermissions.filter(key => permissions[key as keyof PermissionsState]).length

    if (enabledCount === 0) return { status: 'none', text: 'n/a' }
    if (enabledCount === sectorPermissions.length) return { status: 'all', text: 'Todas' }
    return { status: 'partial', text: `${enabledCount}/${sectorPermissions.length}` }
  }

  const handleSelectAllSector = (sectorPermissions: string[], selectAll: boolean) => {
    const updates: Partial<PermissionsState> = {}
    sectorPermissions.forEach(key => {
      if (key in permissions) {
        updates[key as keyof PermissionsState] = selectAll as any
      }
    })
    setPermissions(prev => {
      if (!prev) return prev
      return { ...prev, ...updates }
    })
  }

  // Adicione este estilo para esconder a scrollbar
  const scrollbarHideStyle = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  .scrollbar-hide {
    scroll-behavior: smooth;
  }
`
  const layoutScrollStyle = `
  .content-scroll {
    height: calc(100dvh - 200px); /* ajuste fino se precisar */
    overflow-y: auto;
    -webkit-overflow-scrolling: touch; /* iOS momentum */
    overscroll-behavior: contain;
  }
  .sticky-tabs {
    position: sticky;
    top: 0;
    z-index: 30;
    background: var(--background-color, #fff);
  }
`

  return (
    <PageLayout
      title="Gerenciar Permissões"
      description={
        <div>
          <p className="text-muted-foreground mt-2">Configure as permissões de acesso por setor de forma organizada</p>
          <div className="flex items-center justify-end">
            <Button onClick={handleSave} disabled={isSaving} size="lg">
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar
                </>
              )}
            </Button>
          </div>
        </div>
      }
    >
      <style dangerouslySetInnerHTML={{ __html: scrollbarHideStyle + layoutScrollStyle }} />
      <div className="content-scroll px-1 md:px-0">
        <Tabs defaultValue="gestao">
          <div className="mb-6">
            {/* Mobile: Scroll horizontal profissional */}
            <div className="block lg:hidden">
              <div className="relative">
                {/* Gradientes para indicar scroll */}
                <div
                  className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none opacity-0 transition-opacity duration-300"
                  id="scroll-left-indicator"
                />
                <div
                  className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none opacity-100 transition-opacity duration-300"
                  id="scroll-right-indicator"
                />

                {/* Container com scroll */}
                <div
                  className="overflow-x-auto scrollbar-hide"
                  onScroll={e => {
                    const target = e.target as HTMLDivElement
                    const leftIndicator = document.getElementById('scroll-left-indicator')
                    const rightIndicator = document.getElementById('scroll-right-indicator')

                    if (leftIndicator && rightIndicator) {
                      // Mostrar indicador esquerdo se não estiver no início
                      leftIndicator.style.opacity = target.scrollLeft > 10 ? '1' : '0'

                      // Mostrar indicador direito se não estiver no final
                      const isAtEnd = target.scrollLeft >= target.scrollWidth - target.clientWidth - 10
                      rightIndicator.style.opacity = isAtEnd ? '0' : '1'
                    }
                  }}
                >
                  <TabsList className="inline-flex h-auto p-2 w-auto min-w-full justify-start">
                    <div className="flex gap-3 px-2">
                      {permissionSectors.map(sector => {
                        const sectorPermissionKeys = sector.permissions.map(p => p.key)
                        const sectorStatus = getSectorStatus(sectorPermissionKeys)
                        const Icon = sector.icon

                        return (
                          <TabsTrigger
                            key={sector.id}
                            value={sector.id}
                            className="flex flex-col items-center gap-2 p-4 h-auto min-w-[100px] data-[state=active]:bg-white data-[state=active]:shadow-sm whitespace-nowrap border border-transparent data-[state=active]:border-gray-200 transition-all duration-200 hover:bg-gray-50"
                          >
                            <div className="flex items-center gap-2">
                              <Icon className="w-5 h-5" />
                              <span className="text-sm font-medium">{sector.title}</span>
                            </div>
                            <Badge
                              variant={
                                sectorStatus.status === 'all'
                                  ? 'default'
                                  : sectorStatus.status === 'partial'
                                    ? 'secondary'
                                    : 'outline'
                              }
                              className="text-xs px-2 py-1"
                            >
                              {sectorStatus.text}
                            </Badge>
                          </TabsTrigger>
                        )
                      })}
                    </div>
                  </TabsList>
                </div>
              </div>
            </div>

            {/* Desktop: Grid layout simétrico */}
            <div className="hidden lg:block">
              <TabsList className="grid w-full grid-cols-4 xl:grid-cols-7 gap-2 h-auto p-2">
                {permissionSectors.map(sector => {
                  const sectorPermissionKeys = sector.permissions.map(p => p.key)
                  const sectorStatus = getSectorStatus(sectorPermissionKeys)
                  const Icon = sector.icon

                  return (
                    <TabsTrigger
                      key={sector.id}
                      value={sector.id}
                      className="flex flex-col items-center gap-1 p-3 h-auto data-[state=active]:bg-white"
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-xs font-medium text-center leading-tight">{sector.title}</span>
                      <Badge
                        variant={
                          sectorStatus.status === 'all'
                            ? 'default'
                            : sectorStatus.status === 'partial'
                              ? 'secondary'
                              : 'outline'
                        }
                        className="text-xs px-3 py-0"
                      >
                        {sectorStatus.text}
                      </Badge>
                    </TabsTrigger>
                  )
                })}
              </TabsList>
            </div>
          </div>

          {permissionSectors.map(sector => {
            const sectorPermissionKeys = sector.permissions.map(p => p.key)
            const sectorStatus = getSectorStatus(sectorPermissionKeys)
            const Icon = sector.icon

            return (
              <TabsContent key={sector.id} value={sector.id} className="mt-6">
                <Card className={`${sector.color} transition-all`}>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-white rounded-lg shadow-sm">
                          <Icon className="w-6 h-6" />
                        </div>
                        <div>
                          <CardTitle className="text-xl">{sector.title}</CardTitle>
                          <CardDescription className="text-sm mt-1">{sector.description}</CardDescription>
                        </div>
                      </div>
                      <Badge
                        variant={
                          sectorStatus.status === 'all'
                            ? 'default'
                            : sectorStatus.status === 'partial'
                              ? 'secondary'
                              : 'outline'
                        }
                        className="text-sm px-3 py-1"
                      >
                        {sectorStatus.text} ativas
                      </Badge>
                    </div>

                    <Separator className="mt-4" />

                    <div className="flex items-center justify-between pt-4">
                      <span className="text-sm font-medium">Selecionar todas as permissões deste setor</span>
                      <Switch
                        checked={sectorStatus.status === 'all'}
                        onCheckedChange={checked => handleSelectAllSector(sectorPermissionKeys, checked)}
                      />
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      {sector.permissions.map(permission => (
                        <div
                          key={permission.key}
                          className="flex items-center justify-between p-4 bg-white rounded-lg border hover:shadow-sm transition-all cursor-pointer hover:bg-gray-50 active:bg-gray-100"
                          onClick={() =>
                            handlePermissionChange(
                              permission.key as keyof PermissionsState,
                              !permissions[permission.key as keyof PermissionsState]
                            )
                          }
                        >
                          <div className="flex-1 pointer-events-none">
                            <div className="text-sm font-medium">{permission.label}</div>
                            <p className="text-xs text-muted-foreground mt-1">{permission.description}</p>
                          </div>
                          <div className="flex items-center gap-2 ml-3 pointer-events-none">
                            {permissions[permission.key as keyof PermissionsState] && (
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                            )}
                            <Switch
                              id={permission.key}
                              checked={permissions[permission.key as keyof PermissionsState] as boolean}
                              onCheckedChange={checked =>
                                handlePermissionChange(permission.key as keyof PermissionsState, checked)
                              }
                              className="pointer-events-auto"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )
          })}
        </Tabs>
      </div>
    </PageLayout>
  )
}
